import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Store } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { rm } from "../lib/format";
import { EASE } from "../lib/motion";
import { WishlistHeart } from "../components/wishlist/WishlistHeart";
import { EmptyState, PageLoader } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";
import { ProductImage } from "../components/ProductCard";
import { ListingTitle } from "../components/ListingTitle";
import { trackMarketplaceListingImpression } from "../lib/marketplaceImpressions";
import { marketplaceShopPath } from "../lib/marketplaceShopPath";
import { StarRating } from "../components/reviews/StarRating";
import { CategoryFilterMobileControls, CategoryFilterSidebar } from "../components/catalog/CategoryFilter";
import type { CatalogCategoryFlat } from "../lib/catalogCategories";

type ListingItem = {
  id: string;
  slug: string;
  title: string;
  price: number;
  quantity: number;
  images: string[];
  seller: { shopName: string; shopSlug: string };
  avgRating?: number;
  ratingCount?: number;
};

export function Market() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => globalThis.clearTimeout(t);
  }, [search]);

  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api<{ categories: CatalogCategoryFlat[] }>("/catalog/categories"),
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["market-listings", debouncedSearch, categoryId, sort],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: "60", sort });
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (categoryId) params.set("categoryId", categoryId);
      return api<{ items: ListingItem[]; total: number }>(`/marketplace/listings?${params}`);
    },
  });

  const items = data?.items ?? [];

  useEffect(() => {
    for (const item of items) {
      trackMarketplaceListingImpression(item.id, "LISTING");
    }
  }, [items]);

  const searchField = (
    <input
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search listings…"
      className="h-[42px] w-full min-w-0 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none transition-colors placeholder:text-white/30 focus:border-brand-pink/60"
    />
  );

  const sortField = (
    <select
      value={sort}
      onChange={(e) => setSort(e.target.value)}
      aria-label="Sort listings"
      className="h-[42px] w-[6.25rem] max-w-[6.25rem] rounded-xl border border-white/10 bg-surface py-2 pl-2 pr-6 text-[11px] outline-none lg:w-auto lg:max-w-none lg:min-w-[5.75rem] lg:py-2.5 lg:pl-2.5 lg:pr-7 lg:text-xs"
    >
      <option value="newest">Newest</option>
      <option value="price_asc">Price ↑</option>
      <option value="price_desc">Price ↓</option>
      <option value="name">A–Z</option>
    </select>
  );

  const errMsg = isError && error instanceof ApiError ? error.message : null;

  return (
    <div className="min-w-0 space-y-4 px-4 pb-10 pt-4 lg:px-6">
      <PageHeader
        title="Marketplace"
        description="Buy singles and sealed product from verified collectors."
      />

      {errMsg ? (
        <EmptyState icon={Store} title="Marketplace unavailable" hint={errMsg} />
      ) : (
        <>
          <div className="sticky top-[52px] z-30 min-w-0 max-w-full py-2 lg:hidden">
            {categories?.categories ? (
              <CategoryFilterMobileControls
                categories={categories.categories}
                selectedId={categoryId}
                onSelect={setCategoryId}
                search={searchField}
                sort={sortField}
              />
            ) : (
              <div className="flex min-w-0 gap-2">
                {searchField}
                {sortField}
              </div>
            )}
          </div>

          <div className="lg:flex lg:gap-8">
            <aside className="mb-4 hidden w-52 shrink-0 lg:block">
              <div className="sticky top-24 space-y-4">
                <div className="flex gap-2">{searchField}</div>
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">Sort</p>
                  <div className="flex">{sortField}</div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-white/40">Categories</p>
                  {categories?.categories ? (
                    <CategoryFilterSidebar
                      categories={categories.categories}
                      selectedId={categoryId}
                      onSelect={setCategoryId}
                    />
                  ) : null}
                </div>
              </div>
            </aside>

            <div className="min-w-0 flex-1">
              {data?.total != null && !isLoading && (
                <p className="mb-3 hidden text-[11px] text-white/40 lg:block">
                  {data.total} item{data.total === 1 ? "" : "s"}
                  {debouncedSearch ? ` matching “${debouncedSearch}”` : ""}
                </p>
              )}

              {isLoading ? (
                <PageLoader />
              ) : items.length ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {items.map((item, i) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ delay: (i % 5) * 0.04, duration: 0.38, ease: EASE.outExpo }}
                    >
                      <Link
                        to={`/market/${item.slug}?shop=${encodeURIComponent(item.seller.shopSlug)}`}
                        className="relative block overflow-hidden rounded-2xl border border-white/5 bg-surface text-left transition-all lg:hover:-translate-y-1 lg:hover:border-brand-pink/30"
                      >
                        <div className="absolute right-2 top-2 z-10">
                          <WishlistHeart kind="MARKETPLACE" listingId={item.id} />
                        </div>
                        <ProductImage src={item.images[0] ?? null} alt="" className="aspect-square w-full" />
                        <div className="p-2.5">
                          <ListingTitle className="text-[11px] leading-4">{item.title}</ListingTitle>
                          <Link
                            to={marketplaceShopPath(item.seller.shopSlug)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 block truncate text-[10px] text-white/45 hover:text-brand-pink"
                          >
                            {item.seller.shopName}
                          </Link>
                          {(item.ratingCount ?? 0) > 0 && (
                            <div className="mt-1 flex items-center gap-1">
                              <StarRating value={Math.round(item.avgRating ?? 0)} readOnly size={11} />
                              <span className="text-[9px] text-white/40">({item.ratingCount})</span>
                            </div>
                          )}
                          <p className="mt-1 font-display text-sm font-bold text-brand-pink">{rm(item.price)}</p>
                          {item.quantity > 0 && item.quantity <= 5 ? (
                            <p className="mt-0.5 text-right text-[10px] text-amber-300/90">{item.quantity} left</p>
                          ) : null}
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Store}
                  title="No listings match"
                  hint="Try a different search or category. New listings appear after staff approval."
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
